import type { ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { LoadingState } from '@/shared/components/feedback/LoadingState';
import { toModelId } from '@/shared/types/common.types';
import { parsePositiveIntegerParam } from '@/shared/utils/routeParams';
import { useCapabilities } from '../hooks/useCapabilities';
import { useModelOwner } from '../hooks/useModelOwner';

/**
 * `/projects`, the flat list of every model. With the Core workspace on, a model only exists
 * inside a project, so the list is replaced by the projects themselves; without the workspace
 * there is no project to hold a model, and the list stays.
 */
export function LegacyModelListRoute({ fallback }: { readonly fallback: ReactNode }): React.JSX.Element {
  const { capabilities, isPending } = useCapabilities();

  if (isPending) return <LoadingState label="Opening…" testID="legacy-route-loading" />;

  return capabilities.projectWorkspace ? <Navigate to={ROUTES.workspace} replace /> : <>{fallback}</>;
}

/**
 * `/projects/:modelId`, a model opened on its own — an old bookmark, or a link from a page that
 * knows only the model, such as an audit result. With the workspace on it moves into the
 * project that owns the model, keeping the query (`?tab=…&instance=…`). A model no project
 * links still opens on its own, so nothing that worked before stops working.
 */
export function LegacyModelRoute({ fallback }: { readonly fallback: ReactNode }): React.JSX.Element {
  const { projectId } = useParams<{ projectId: string }>();
  const { search } = useLocation();
  const { capabilities, isPending } = useCapabilities();
  const parsed = parsePositiveIntegerParam(projectId);
  const modelId = parsed === null ? null : toModelId(parsed);
  const owner = useModelOwner(modelId, capabilities.projectWorkspace);

  if (isPending || owner.state === 'pending') {
    return <LoadingState label="Opening the model's project…" testID="legacy-route-loading" />;
  }

  if (owner.state === 'found' && modelId !== null) {
    return (
      <Navigate to={`${ROUTES.projectModel(owner.project.id, Number(modelId))}${search}`} replace />
    );
  }

  return <>{fallback}</>;
}
